/**
 * Global oscillator banner (spec §11.1).
 *
 * Slim 32px status bar rendered above the tab nav. Clicking the banner
 * navigates to the Market tab so the owner can drill into the full reading.
 */

import { ChevronRight } from 'lucide-react';
import useOscillatorSignal from '../hooks/useOscillatorSignal';
import {
  canonicalZone,
  zoneLabel,
  zoneTokens,
  directionGlyph,
} from '../lib/oscillatorZone';

function formatValue(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const num = Number(v);
  const sign = num > 0 ? '+' : '';
  return `${sign}${num.toFixed(1)}`;
}

export default function OscillatorBanner({ onClick }) {
  const { status, signal } = useOscillatorSignal();

  if (status === 'loading') {
    return (
      <div className="h-8 border-b border-slate-700/50 bg-slate-800/20">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center text-xs text-slate-500">
          Loading market oscillator…
        </div>
      </div>
    );
  }

  if (status === 'empty' || status === 'error' || !signal) {
    return (
      <div className="h-8 border-b border-slate-700/50 bg-slate-800/20">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center text-xs text-slate-500">
          <span className="inline-block w-2 h-2 rounded-full bg-slate-600 mr-2" />
          Market oscillator unavailable
        </div>
      </div>
    );
  }

  const zone = canonicalZone(signal.zone, signal.value);
  const tokens = zoneTokens(zone);
  const label = zoneLabel(zone);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full h-8 border-b ${tokens.borderClass} ${tokens.bgSoft} hover:brightness-110 transition-all text-left group`}
      aria-label={`S&P breadth oscillator: ${label} at ${formatValue(signal.value)}`}
    >
      <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center gap-3 text-xs">
        <span className={`inline-block w-2 h-2 rounded-full ${tokens.dotClass} flex-shrink-0`} />
        <span className="font-semibold tracking-wide text-slate-300 uppercase text-[10px]">
          Mkt
        </span>
        <span className={`font-mono font-bold ${tokens.textClass} text-sm`}>
          {formatValue(signal.value)}
        </span>
        <span className={`font-semibold ${tokens.textClass} uppercase tracking-wide`}>
          {label}
        </span>
        {signal.direction_change && (
          <span className="text-slate-400">
            {directionGlyph(signal.direction_change)}{' '}
            {signal.previous_value != null && (
              <span className="font-mono text-slate-500">
                from {formatValue(signal.previous_value)}
              </span>
            )}
          </span>
        )}
        {tokens.tone && (
          <span className="hidden md:inline text-slate-500 italic">
            {tokens.tone}
          </span>
        )}
        <span className="flex-1" />
        <span className="hidden sm:inline text-slate-500">
          {signal.trade_date}
        </span>
        <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 transition-colors" />
      </div>
    </button>
  );
}
